// START IMPORTS
import { Badge, CustomIcons, Stack, useTheme } from '@ltvco/refresh-lib/theme';
import { Owner } from '@ltvco/refresh-lib/v1';
import { useFeatureIsOn } from '@ltvco/refresh-lib/vendors';
import {
  Email,
  Radar,
  Person2,
  Phone,
  Work,
  TaskAlt,
} from '@mui/icons-material';
// END IMPORTS

// START HELPER_FUNCTIONS
export const getEmailNavLinkData = (
  potentialOwnersLength: number,
  owner: Owner
) => {
  const isEmailPartialAddress = useFeatureIsOn('OAR-1706');
  return [
    {
      href: '#email-overview',
      sectionId: '#email-overview',
      text: 'Overview',
      reportItemsCount: null,
      hideReportItemsCount: true,
      notificationSection: 'identity',
    },
    {
      href: '#email-fraud-check-section',
      sectionId: '#email-fraud-check-section"',
      text: 'FraudScan Summary',
      reportItemsCount: null,
      hideReportItemsCount: true,
      notificationSection: 'fraud_check',
    },
    {
      href: '#potential-owners-section',
      sectionId: '#potential-owners-section',
      text: 'Potential Owners',
      reportItemsCount: potentialOwnersLength,
      notificationSection: 'owners',
    },
    {
      href: '#possible-photos-section',
      sectionId: '#possible-photos-section',
      text: 'Possible Photos',
      reportItemsCount: owner.images?.length || null,
      notificationSection: 'photos',
    },
    {
      href: '#phone-numbers-section',
      sectionId: '#phone-numbers-section',
      text: 'Phone Numbers',
      reportItemsCount: owner.phones.length,
      notificationSection: 'phones',
    },
    {
      href: '#email-section',
      sectionId: '#email-section',
      text: 'Emails',
      reportItemsCount: owner.emails.length,
      notificationSection: 'emails',
    },
    {
      href: '#address-history-section',
      sectionId: '#address-history-section',
      text: isEmailPartialAddress ? 'Possible Locations' : 'Address History',
      reportItemsCount: owner.addresses.length,
      notificationSection: 'addresses',
    },
    {
      href: '#jobs-section',
      sectionId: '#jobs-section',
      text: 'Professional Background',
      reportItemsCount: owner.jobs.length,
      notificationSection: 'jobs',
    },
    {
      href: '#education-section',
      sectionId: '#education-section',
      text: 'Education',
      reportItemsCount: owner.educations.length,
      notificationSection: 'educations',
    },
    {
      href: '#social-media-section',
      sectionId: '#social-media-section',
      text: 'Social & Websites',
      reportItemsCount: owner.profiles.length + owner.usernames.length,
      notificationSection: 'socials',
    },
    {
      href: '#possible-owned-assets-section',
      sectionId: '#possible-owned-assets-section',
      text: 'Assets',
      reportItemsCount:
        (owner?.ownedAssets?.real_estate?.length ?? 0) +
        (owner?.ownedAssets?.vehicles?.watercraft?.length ?? 0) +
        (owner?.ownedAssets?.vehicles?.aircraft?.length ?? 0) +
        (owner?.ownedAssets?.vehicles?.automobiles?.length ?? 0),
      notificationSection: 'real_estate',
    },
  ];
};

export const getEmailNavLinkDataV2 = (
  potentialOwnersLength: number,
  owner: Owner
) => {
  const theme = useTheme();
  const isInConsolidatedMonitoringTest = useFeatureIsOn('RFRSH-3449');
  const isEmailPartialAddress = useFeatureIsOn('OAR-1706');

  return [
    {
      href: '#email-overview',
      labelText: 'Overview',
      name: 'email-overview',
      icons: [
        <Email
          sx={{ color: theme.palette.primary.main, fontSize: '2.5rem' }}
        />,
      ],
      notificationSection: 'identity',
    },
    {
      href: '#email-fraud-check-section',
      labelText: 'FraudScan Summary',
      icons: [
        <Radar
          sx={{ color: theme.palette.primary.main, fontSize: '1.4rem' }}
        />,
      ],
      hideReportItemsCount: true,
      name: 'email-fraud-check-section',
      notificationSection: 'fraud_check',
    },
    {
      labelText: 'Potential Owners',
      name: 'potential-owners-section',
      icons: [
        <Person2
          sx={{ color: theme.palette.primary.main, fontSize: '1.4rem' }}
        />,
      ],
      childItems: [
        {
          href: '#potential-owners-section',
          labelText: 'Potential Owners',
          reportItemsCount: potentialOwnersLength,
          name: 'potential-owners-section',
          notificationSection: 'owners',
        },
        {
          href: '#possible-photos-section',
          labelText: 'Possible Photos',
          reportItemsCount: owner.images?.length || null,
          name: 'possible-photos-section',
          notificationSection: 'photos',
        },
      ],
    },
    {
      labelText: 'Contact Information',
      icons: [
        <Phone
          sx={{ color: theme.palette.primary.main, fontSize: '1.4rem' }}
        />,
      ],
      childItems: [
        {
          href: '#phone-numbers-section',
          labelText: 'Phone Numbers',
          reportItemsCount: Math.max(owner.phones.length - 1, 0),
          name: 'phone-numbers-section',
          notificationSection: 'phones',
        },
        {
          href: '#email-section',
          labelText: 'Email Addresses',
          reportItemsCount: owner.emails.length,
          name: 'email-section',
          notificationSection: 'emails',
        },
        {
          href: '#address-history-section',
          labelText: isEmailPartialAddress
            ? 'Possible Locations'
            : 'Address History',
          reportItemsCount: owner.addresses.length,
          name: 'address-history-section',
          notificationSection: 'addresses',
        },
        {
          href: '#social-media-section',
          sectionId: '#social-media-section',
          labelText: 'Social Media',
          reportItemsCount: owner.profiles.length + owner.usernames.length,
          name: 'social-media-section',
          notificationSection: 'social',
        },
      ],
    },
    ...(isInConsolidatedMonitoringTest
      ? [
          {
            labelText: 'Data Breaches',
            name: 'dark-web-email-section',
            showIcon: true,
            hideReportItemsCount: true,
            href: '#dark-web-email-section',
            icons: [
              <CustomIcons.DarkWebIcon
                sx={{ color: theme.palette.primary.main, fontSize: '1.4rem' }}
              />,
              <Stack spacing={1} direction="row" alignItems="center">
                <Badge
                  sx={{
                    backgroundColor: 'premium.main',
                    color: 'premium.contrastText',
                    borderRadius: 100,
                    fontSize: '.875rem',
                    paddingInline: 0.8125,
                    lineHeight: 1.6,
                  }}
                >
                  New
                </Badge>
              </Stack>,
            ],
          },
        ]
      : []),

    {
      labelText: 'Jobs & Educations',
      name: 'jobs-educations-section',
      icons: [
        <Work sx={{ color: theme.palette.primary.main, fontSize: '1.4rem' }} />,
      ],
      childItems: [
        {
          href: '#jobs-section',
          sectionId: '#jobs-section',
          labelText: 'Jobs',
          reportItemsCount: owner.jobs.length,
          name: 'jobs-section',
          notificationSection: 'jobs',
        },
        {
          href: '#education-section',
          sectionId: '#education-section',
          labelText: 'Education',
          reportItemsCount: owner.educations.length,
          name: 'education-section',
          notificationSection: 'educations',
        },
      ],
    },
    {
      labelText: 'Assets',
      icons: [<CustomIcons.OwnedAssetsIcon color="primary" fontSize="small" />],
      href: '#possible-owned-assets-section',
      name: 'assets',
      reportItemsCount:
        (owner?.ownedAssets?.real_estate?.length ?? 0) +
        (owner?.ownedAssets?.vehicles?.watercraft?.length ?? 0) +
        (owner?.ownedAssets?.vehicles?.aircraft?.length ?? 0) +
        (owner?.ownedAssets?.vehicles?.automobiles?.length ?? 0),
      notificationSection: ['real_estate', 'automobiles', 'vehicles'],
    },
    {
      labelText: 'Next Steps',
      name: 'next-steps',
      icons: [
        <TaskAlt
          sx={{ color: theme.palette.primary.main, fontSize: '1.4rem' }}
        />,
      ],
      childItems: [
        {
          href: '#Notes-section',
          labelText: 'Notes',
          hideReportItemsCount: true,
          name: 'notes-section',
        },
        {
          href: '#related-reports-section',
          labelText: 'Related Reports',
          hideReportItemsCount: true,
          name: 'related-reports-section',
        },
        {
          href: '#report-rating',
          labelText: 'Report Rating',
          hideReportItemsCount: true,
          name: 'report-rating',
        },
      ],
    },
  ];
};
// END HELPER_FUNCTIONS
